import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, Package, Shield, FolderKanban, FileSignature, GitMerge } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const MODULE_ICONS = {
  grc: Shield,
  pmo: FolderKanban,
  contracts: FileSignature,
  ma: GitMerge
};

const MODULE_NAMES = {
  grc: 'Governance, Risk & Compliance',
  pmo: 'Project Management Office',
  contracts: 'Contract Manager',
  ma: 'Mergers & Acquisitions'
};

const MODULE_COLORS = {
  grc: 'from-emerald-500 to-green-600',
  pmo: 'from-blue-500 to-indigo-600',
  contracts: 'from-purple-500 to-violet-600',
  ma: 'from-amber-500 to-orange-600'
};

/**
 * ModuleAccessDenied component
 * Displays when user tries to access a module they don't have subscription for
 */
export const ModuleAccessDenied = ({ module, moduleName, reason }) => {
  const navigate = useNavigate();
  const Icon = MODULE_ICONS[module] || Lock;
  const displayName = moduleName || MODULE_NAMES[module] || module;
  const gradientColor = MODULE_COLORS[module] || 'from-slate-500 to-slate-600';

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <Card className="max-w-md w-full bg-slate-900 border-slate-700">
        <CardContent className="pt-8 pb-6 text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${gradientColor} flex items-center justify-center opacity-50`}>
            <Icon className="h-10 w-10 text-white" />
          </div>
          
          <div className="mb-2">
            <Lock className="h-6 w-6 text-amber-400 mx-auto" />
          </div>
          
          <h2 className="text-xl font-bold text-white mb-2">
            {displayName}
          </h2>
          
          <p className="text-slate-400 text-sm mb-6">
            {reason || 'This module is not included in your current subscription.'}
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/pricing')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              <Package className="h-4 w-4 mr-2" />
              View Plans & Upgrade
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-full"
            >
              Go Back
            </Button>
          </div>
          
          <p className="text-xs text-slate-500 mt-6">
            Need help? Contact support@simplif-i.com
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * withModuleAccess HOC
 * Wraps a component to check module access and show upgrade prompt if denied
 */
export const withModuleAccess = (WrappedComponent, module) => {
  return function ModuleAccessWrapper(props) {
    const [accessDenied, setAccessDenied] = React.useState(null);
    
    // Check for module access error in API responses
    React.useEffect(() => {
      const handleAccessDenied = (event) => {
        if (event.detail?.module === module) {
          setAccessDenied(event.detail);
        }
      };
      
      window.addEventListener('moduleAccessDenied', handleAccessDenied);
      return () => window.removeEventListener('moduleAccessDenied', handleAccessDenied);
    }, []);
    
    if (accessDenied) {
      return (
        <ModuleAccessDenied 
          module={accessDenied.module}
          moduleName={accessDenied.module_name}
          reason={accessDenied.reason}
        />
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

/**
 * Axios interceptor to handle 403 module access denied errors
 * Add this to your axios setup
 */
export const setupModuleAccessInterceptor = (axios) => {
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'module_access_denied') {
          // Dispatch custom event for components to handle
          window.dispatchEvent(new CustomEvent('moduleAccessDenied', {
            detail: {
              module: detail.module,
              module_name: detail.module_name,
              reason: detail.reason,
              upgrade_url: detail.upgrade_url
            }
          }));
        }
      }
      return Promise.reject(error);
    }
  );
};

export default ModuleAccessDenied;
